import * as React from 'react'
import { useUserQuery } from 'auth/hooks'
import { isLoggedInQuerySelector } from 'auth/selectors'
import { usePaymentsDialog } from '../../components/payments-dialog-wrapper-context'
import type { PAYMENT_ENTRY_POINTS } from '../../payments-dialog/analytics'
import { savePaymentModalEntryPoint } from '../../payments-dialog/analytics'
import { PaymentStatus, SubscriptionTypeUpgrade } from '@repo/graphql-types'
import type { UpgradeSource } from '../../upgrade-sources'
import { B } from '../app-routes'
import { useActivateTrial } from './use-activate-trial'
import type { ActivateTrialReturn } from './use-activate-trial'

type SubscriptionVariant = 'all-access' | 'studio-ai'

function getActivateTo(subscriptionType: SubscriptionVariant) {
  return subscriptionType === 'studio-ai'
    ? SubscriptionTypeUpgrade.StudioAiYearly
    : SubscriptionTypeUpgrade.AllAccessYearlyPromo
}

function getDefaultPlan({
  subscriptionType,
}: {
  subscriptionType: SubscriptionVariant
}): 'aiStudio' | 'freeTrialYearly' | 'yearly' {
  if (subscriptionType === 'studio-ai') {
    return 'aiStudio'
  }

  return 'yearly'
}

export function getLoggedOutActivateTrialHref({
  subscriptionType,
  isLoggedIn,
  isEligibleForAllAccessFreeTrial,
}: {
  subscriptionType: SubscriptionVariant
  isLoggedIn: boolean
  isEligibleForAllAccessFreeTrial?: boolean
}) {
  if (!isLoggedIn || isEligibleForAllAccessFreeTrial) {
    return B.YEARLY_PROMO
  }

  if (subscriptionType === 'studio-ai') {
    return B.FREE_TRIAL_STUDIO_AI_YEARLY
  }

  return B.YEARLY_PROMO
}

export function useActivateTrialController({
  entryPoint,
  onSettled,
  noReload = false,
  subscriptionType = 'all-access',
  upgradeSource,
  shouldApplyBonus = false,
}: {
  entryPoint: PAYMENT_ENTRY_POINTS
  onSettled?: () => void
  noReload?: boolean
  subscriptionType?: SubscriptionVariant
  upgradeSource: UpgradeSource
  shouldApplyBonus?: boolean
}) {
  const { open } = usePaymentsDialog()
  const {
    data,
    refetch,
    isLoading: userIsLoading,
  } = useUserQuery(undefined, { refetchOnWindowFocus: false })
  const [result, setResult] = React.useState<ActivateTrialReturn | null>(null)
  const isUserLoggedIn = isLoggedInQuerySelector(data)

  const { isProcessing, activateTrial } = useActivateTrial({ subscriptionType, shouldApplyBonus })

  const openCheckout = React.useCallback(
    (defaultPlan: 'aiStudio' | 'freeTrialYearly' | 'yearly') => {
      if (typeof window !== 'undefined' && typeof window.cfOpenPaymentsDialog === 'function') {
        window.cfOpenPaymentsDialog({
          defaultPlan,
          useCase: 'one-click-upgrade',
        })
        return
      }

      open(defaultPlan)
    },
    [open],
  )

  const handleLoggedInTrigger = React.useCallback(async () => {
    if (isProcessing || userIsLoading) {
      return
    }

    savePaymentModalEntryPoint(entryPoint)

    const user = await refetch()
    const userData = user.data?.me?.user

    if (!userData) {
      return
    }

    const userHasAllAccess = userData.subscriptionAccess.allAccess.isSubscribed
    const isEligibleForAllAccessFreeTrial =
      userData.subscriptionAccess.allAccess.isEligibleForFreeTrial
    const isEligibleForAllAccessYearlyUpgrade =
      userData.subscriptionAccess.allAccess.isEligibleForYearlyUpgrade

    const userHasStudioAi = userData.subscriptionAccess.studioAi.isSubscribed
    const isEligibleForStudioAiFreeTrial =
      userData.subscriptionAccess.studioAi.isEligibleForFreeTrial
    const isEligibleForStudioAiYearlyUpgrade =
      userData.subscriptionAccess.studioAi.isEligibleForYearlyUpgrade

    const defaultPlan = getDefaultPlan({
      subscriptionType,
    })

    if (
      subscriptionType === 'all-access' &&
      userHasAllAccess &&
      !isEligibleForAllAccessFreeTrial &&
      !isEligibleForAllAccessYearlyUpgrade
    ) {
      window.location.reload()
      return
    }

    if (
      subscriptionType === 'studio-ai' &&
      userHasStudioAi &&
      !isEligibleForStudioAiFreeTrial &&
      !isEligibleForStudioAiYearlyUpgrade
    ) {
      window.location.reload()
      return
    }

    if (
      (!userHasAllAccess && subscriptionType === 'all-access') ||
      (!userHasStudioAi && subscriptionType === 'studio-ai')
    ) {
      openCheckout(defaultPlan)
      return
    }

    const nextResult = await activateTrial({ upgradeSource })

    if (nextResult.error?.type === 'noSubscriptionId') {
      openCheckout(defaultPlan)
      return
    }

    setResult(nextResult)
  }, [
    activateTrial,
    entryPoint,
    isProcessing,
    openCheckout,
    refetch,
    subscriptionType,
    upgradeSource,
    userIsLoading,
  ])

  const closeResult = React.useCallback(() => {
    setResult(null)
  }, [])

  const handleSuccessOpenChange = React.useCallback(
    (open: boolean) => {
      if (open) {
        return
      }

      closeResult()

      const event = new CustomEvent<{
        intent_status: string
        subscription_type: string | null | undefined
      }>('cf_intent_status_updated', {
        detail: {
          intent_status: 'succeeded',
          subscription_type: getActivateTo(subscriptionType),
        },
      })

      document.dispatchEvent(event)
      onSettled?.()

      if (!noReload) {
        window.location.reload()
      }
    },
    [closeResult, noReload, onSettled, subscriptionType],
  )

  return {
    closeResult,
    getLoggedOutHref: () =>
      getLoggedOutActivateTrialHref({
        subscriptionType,
        isLoggedIn: isUserLoggedIn,
        isEligibleForAllAccessFreeTrial:
          data?.me?.user?.subscriptionAccess.allAccess.isEligibleForFreeTrial,
      }),
    handleLoggedInTrigger,
    handleSuccessOpenChange,
    isActivated:
      subscriptionType === 'all-access'
        ? Boolean(
            data?.me?.user?.subscriptionAccess.allAccess.isSubscribed &&
            data?.me?.user?.subscriptionAccess.allAccess.isEligibleForYearlyUpgrade === false,
          )
        : Boolean(
            data?.me?.user?.subscriptionAccess.studioAi.isSubscribed &&
            data?.me?.user?.subscriptionAccess.studioAi.isEligibleForYearlyUpgrade === false,
          ),
    isPending: isProcessing || userIsLoading,
    isUserLoggedIn,
    isSuccess: result?.paymentsStatus === PaymentStatus.Succeeded,
    result,
  }
}
